import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  ExportOutlined,
} from "@ant-design/icons";

interface CalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

declare global {
  interface Window {
    Desmos?: any;
  }
}

const MIN_W = 350;
const MIN_H = 400;
const EXPANDED = { w: 800, h: 700 };
const COLLAPSED = { w: 450, h: 600 };

const DESMOS_OPTIONS = {
  keypad: true,
  expressions: true,
  settingsMenu: true,
  degreeMode: false,
  showResetButtonOnGraphpaper: false,
  showMenuBar: true,
  showHamburgerMenu: true,
  showLinkToUserGuide: false,
  showLogo: true,
};

const DESMOS_SCRIPT_URL =
  "https://www.desmos.com/api/v1.11/calculator.js?apiKey=dcb31709b452b1cf9dc26972add0fda6";

const CalculatorModal: React.FC<CalculatorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const calculatorRef = useRef<HTMLDivElement | null>(null);
  const desmosInstanceRef = useRef<any>(null);
  const popupRef = useRef<Window | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isPoppedOut, setIsPoppedOut] = useState(false);

  const [pos, setPos] = useState({ x: 80, y: 80 });
  const [size, setSize] = useState(EXPANDED);

  const dragRef = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);
  const resizeRef = useRef<{
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    edge: string;
  } | null>(null);

  // Desmos initialization (inline mode)
  useEffect(() => {
    if (!isOpen || isPoppedOut) return;

    if (!window.Desmos) {
      const script = document.createElement("script");
      script.src = DESMOS_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        if (window.Desmos && calculatorRef.current) {
          desmosInstanceRef.current = window.Desmos.GraphingCalculator(
            calculatorRef.current,
            DESMOS_OPTIONS
          );
        }
      };
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    } else if (window.Desmos && calculatorRef.current) {
      desmosInstanceRef.current = window.Desmos.GraphingCalculator(
        calculatorRef.current,
        DESMOS_OPTIONS
      );
    }

    return () => {
      if (desmosInstanceRef.current) {
        desmosInstanceRef.current.destroy();
        desmosInstanceRef.current = null;
      }
    };
  }, [isOpen, isPoppedOut]);

  // Resize Desmos when size changes
  useEffect(() => {
    if (desmosInstanceRef.current) {
      desmosInstanceRef.current.resize();
    }
  }, [size.w, size.h]);

  // Cleanup popup on unmount or close
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // When parent closes while popped out, close popup
  useEffect(() => {
    if (!isOpen && popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
      setIsPoppedOut(false);
    }
  }, [isOpen]);

  // Pop out to a new window
  const handlePopOut = useCallback(() => {
    // Save current Desmos state before destroying
    const savedState = desmosInstanceRef.current?.getState?.();

    if (desmosInstanceRef.current) {
      desmosInstanceRef.current.destroy();
      desmosInstanceRef.current = null;
    }

    const popup = window.open(
      "",
      "desmos-calculator",
      `width=${size.w},height=${size.h},resizable=yes,scrollbars=no`
    );
    if (!popup) return;

    popupRef.current = popup;
    setIsPoppedOut(true);

    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Calculator</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          html, body { width: 100%; height: 100%; overflow: hidden; }
          #calculator { width: 100%; height: 100%; }
        </style>
      </head>
      <body>
        <div id="calculator"></div>
        <script src="${DESMOS_SCRIPT_URL}"><\/script>
        <script>
          window.addEventListener('load', function() {
            var container = document.getElementById('calculator');
            var calc = Desmos.GraphingCalculator(container, ${JSON.stringify(DESMOS_OPTIONS)});
            var savedState = ${savedState ? JSON.stringify(JSON.stringify(savedState)) : "null"};
            if (savedState) {
              try { calc.setState(JSON.parse(savedState)); } catch(e) {}
            }
            window.addEventListener('resize', function() { calc.resize(); });
            window.__desmosCalc = calc;
          });
        <\/script>
      </body>
      </html>
    `);
    popup.document.close();

    // Monitor popup close
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        popupRef.current = null;
        setIsPoppedOut(false);
      }
    }, 500);
  }, [size]);

  // Drag handlers
  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: pos.x,
        startPosY: pos.y,
      };
      document.body.style.userSelect = "none";

      const onDragMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        const newX = dragRef.current.startPosX + dx;
        const newY = dragRef.current.startPosY + dy;
        setPos({
          x: Math.max(-size.w + 100, Math.min(newX, window.innerWidth - 100)),
          y: Math.max(0, Math.min(newY, window.innerHeight - 50)),
        });
      };

      const onDragEnd = () => {
        dragRef.current = null;
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onDragMove);
        document.removeEventListener("mouseup", onDragEnd);
      };

      document.addEventListener("mousemove", onDragMove);
      document.addEventListener("mouseup", onDragEnd);
    },
    [pos, size.w]
  );

  // Resize handlers
  const onResizeStart = useCallback(
    (edge: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startW: size.w,
        startH: size.h,
        edge,
      };
      document.body.style.userSelect = "none";

      const onResizeMove = (ev: MouseEvent) => {
        if (!resizeRef.current) return;
        const dx = ev.clientX - resizeRef.current.startX;
        const dy = ev.clientY - resizeRef.current.startY;
        const newSize = { ...size };

        if (resizeRef.current.edge.includes("r")) {
          newSize.w = Math.max(MIN_W, resizeRef.current.startW + dx);
        }
        if (resizeRef.current.edge.includes("b")) {
          newSize.h = Math.max(MIN_H, resizeRef.current.startH + dy);
        }

        setSize(newSize);
      };

      const onResizeEnd = () => {
        resizeRef.current = null;
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onResizeMove);
        document.removeEventListener("mouseup", onResizeEnd);
      };

      document.addEventListener("mousemove", onResizeMove);
      document.addEventListener("mouseup", onResizeEnd);
    },
    [size]
  );

  const handleToggleExpand = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    setSize(next ? EXPANDED : COLLAPSED);
  };

  if (!isOpen) return null;

  // When popped out, show minimal placeholder
  if (isPoppedOut) {
    return (
      <div
        className="fixed z-50 bg-white rounded-lg shadow-xl px-4 py-3 flex items-center gap-3"
        style={{ top: pos.y, left: pos.x }}
      >
        <span className="font-bold text-sm text-[#22336b] select-none">
          Calculator (Pop-out)
        </span>
        <button
          className="text-sm text-gray-600 hover:text-black font-bold px-2 py-1"
          onClick={() => {
            if (popupRef.current && !popupRef.current.closed) {
              popupRef.current.focus();
            }
          }}
        >
          Focus
        </button>
        <button
          className="text-sm text-gray-600 hover:text-black font-bold px-2 py-1"
          onClick={() => {
            if (popupRef.current && !popupRef.current.closed) {
              popupRef.current.close();
            }
            popupRef.current = null;
            setIsPoppedOut(false);
          }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed z-50 bg-white rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: pos.y,
        left: pos.x,
        width: size.w,
        height: size.h,
      }}
    >
      {/* Drag handle (header) */}
      <div
        className="flex justify-between items-center px-4 py-2 border-b border-gray-200 shrink-0"
        onMouseDown={onDragStart}
        style={{ cursor: dragRef.current ? "grabbing" : "grab" }}
      >
        <span className="font-bold text-base text-[#22336b] select-none">
          Calculator
        </span>
        <div className="flex items-center gap-2">
          <button
            className="w-9 h-9 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-100 transition-colors"
            onClick={handlePopOut}
            title="Pop out to new window"
          >
            <ExportOutlined style={{ fontSize: 18, color: "#555" }} />
          </button>
          <button
            className="w-9 h-9 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-100 transition-colors"
            onClick={handleToggleExpand}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? (
              <FullscreenExitOutlined
                style={{ fontSize: 20, color: "#555" }}
              />
            ) : (
              <FullscreenOutlined style={{ fontSize: 20, color: "#555" }} />
            )}
          </button>
          <button
            className="text-sm text-gray-600 hover:text-black font-bold px-2 py-1"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {/* Desmos container */}
      <div ref={calculatorRef} className="w-full flex-1" />

      {/* Resize handles */}
      <div
        className="absolute right-0 top-0 w-1.5 h-full cursor-ew-resize"
        onMouseDown={onResizeStart("r")}
      />
      <div
        className="absolute bottom-0 left-0 w-full h-1.5 cursor-ns-resize"
        onMouseDown={onResizeStart("b")}
      />
      <div
        className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize"
        onMouseDown={onResizeStart("rb")}
      />
    </div>
  );
};

export default CalculatorModal;
